/**
 * Dashboard: "המשימות שלי" — my open tasks with their titles, prominent at the top (owner request 2026-09-30:
 * a small count was easy to miss). Overdue first, then today, then by date, then undated. ✓ marks done here.
 */
import { Link } from 'react-router';
import { doc } from 'firebase/firestore';
import { toast } from 'sonner';
import { Check, ChevronLeft, ListTodo } from 'lucide-react';
import { auth, db } from '@/lib/firebase';
import { stampedUpdate } from '@/lib/write';
import { hebrewError } from '@/lib/errors';
import { toDateKey } from '@/lib/dates';
import { useTasks } from '@/lib/moneyData';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

const MAX = 6;

export function MyTasksCard() {
  const me = auth.currentUser?.uid ?? '';
  const tasks = useTasks(true, 'open');
  const today = toDateKey(new Date());
  const mine = tasks.data
    .filter((t) => t.status === 'open' && (t.assigneeUid === me || (!t.assigneeUid && t.createdBy === me)))
    .sort((a, b) => (a.dueDateKey ?? '9999').localeCompare(b.dueDateKey ?? '9999'));
  if (tasks.loading || !mine.length) return null;
  const urgent = mine.filter((t) => t.dueDateKey && t.dueDateKey <= today).length;

  const done = async (id: string) => {
    try { await stampedUpdate(doc(db, 'tasks', id), { status: 'done', doneBy: me, doneAt: new Date() }); toast.success('בוצע'); }
    catch (e) { toast.error(hebrewError(e)); }
  };

  return (
    <Card className={cn('lg:col-span-3', urgent ? 'border-gold ring-2 ring-gold/30' : 'border-primary/30')}>
      <CardHeader className="flex-row items-center justify-between gap-2">
        <CardTitle className="flex flex-wrap items-center gap-2 text-xl">
          <ListTodo className="size-6 text-primary" /> המשימות שלי
          <Badge tone="primary"><bdi>{mine.length}</bdi></Badge>
          {urgent ? <Badge tone="gold"><bdi>{urgent}</bdi> להיום / באיחור</Badge> : null}
        </CardTitle>
        <Link to="/tasks" className="inline-flex items-center gap-1 text-sm text-primary-light hover:underline">לכל המשימות <ChevronLeft className="size-4" /></Link>
      </CardHeader>
      <CardContent>
        <ul className="flex flex-col gap-2">
          {mine.slice(0, MAX).map((t) => {
            const late = !!t.dueDateKey && t.dueDateKey < today;
            const isToday = t.dueDateKey === today;
            return (
              <li key={t.id} className={cn('flex items-start gap-3 rounded-xl border bg-surface px-3 py-2.5', late ? 'border-danger/40 bg-danger-soft/40' : isToday ? 'border-gold/60 bg-gold-soft/40' : 'border-border')}>
                <button type="button" onClick={() => done(t.id)} className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full border-2 border-primary text-primary hover:bg-primary-soft" title="סימון שבוצע" aria-label={`סימון שבוצע: ${t.title}`}>
                  <Check className="size-4" />
                </button>
                <Link to="/tasks" className="min-w-0 flex-1">
                  <div className="text-[17px] leading-snug font-semibold">{t.title}</div>
                  {t.notes ? <div className="line-clamp-2 text-sm text-ink-2">{t.notes}</div> : null}
                  <div className="mt-1 text-xs">
                    {late ? <Badge tone="danger">באיחור · <bdi>{t.dueDateKey!.split('-').reverse().join('/')}</bdi></Badge>
                      : isToday ? <Badge tone="warning">היום</Badge>
                      : t.dueDateKey ? <span className="text-muted">עד <bdi>{t.dueDateKey.split('-').reverse().join('/')}</bdi></span>
                      : <span className="text-muted">בלי תאריך</span>}
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
        {mine.length > MAX ? <Link to="/tasks" className="mt-2 block text-sm text-primary-light hover:underline">ועוד <bdi>{mine.length - MAX}</bdi> משימות…</Link> : null}
      </CardContent>
    </Card>
  );
}
